import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Heart, CreditCard, Calendar, DollarSign, CheckCircle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Button from './Button';
import { STRIPE_CONFIG, DONATION_AMOUNTS, validateDonationAmount } from '../config/stripe';
import './DonatePage.css';

// Helper function to submit donation to Firestore
const submitDonationToFirestore = async (donationData: any) => {
  try {
    const response = await fetch(`${STRIPE_CONFIG.functionsUrl}/submitDonation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(donationData)
    });

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to submit donation');
    }

    return result;
  } catch (error) {
    console.error('Error submitting donation to Firestore:', error);
    throw error;
  }
};

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_CONFIG.publishableKey);

interface DonationForm {
  amount: number;
  customAmount: string;
  isRecurring: boolean;
  frequency: 'monthly' | 'yearly';
  donorName: string;
  donorEmail: string;
  message: string;
  anonymous: boolean;
}

type DonationStep = 'details' | 'payment';

const DonatePage: React.FC = () => {
  const [formData, setFormData] = useState<DonationForm>({
    amount: 50,
    customAmount: '',
    isRecurring: false,
    frequency: 'monthly',
    donorName: '',
    donorEmail: '',
    message: '',
    anonymous: false
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [clientSecret, setClientSecret] = useState<string>('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [currentStep, setCurrentStep] = useState<DonationStep>('details');


  const handleAmountChange = (amount: number) => {
    setFormData(prev => ({ ...prev, amount, customAmount: '' }));
  };

  const handleCustomAmountChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    setFormData(prev => ({ 
      ...prev, 
      customAmount: value,
      amount: numValue > 0 ? numValue : prev.amount 
    }));
  };

  const handleInputChange = (field: keyof DonationForm, value: string | boolean | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getEffectiveAmount = useCallback(() => {
    return formData.customAmount ? parseFloat(formData.customAmount) : formData.amount;
  }, [formData.customAmount, formData.amount]);

  // Create payment intent when on payment step and form data changes
  useEffect(() => {
    if (currentStep !== 'payment') {
      setShowPaymentForm(false);
      setClientSecret('');
      return;
    }

    const createPaymentIntent = async () => {
      const amount = getEffectiveAmount();
      
      if (!validateDonationAmount(amount) || !formData.donorEmail) {
        setShowPaymentForm(false);
        setClientSecret('');
        return;
      }

      setIsProcessing(true);
      setError('');

      try {
        const response = await fetch(`${STRIPE_CONFIG.functionsUrl}/createPaymentIntent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: Math.round(amount * 100), // Convert to cents
            currency: 'usd',
            isRecurring: formData.isRecurring,
            frequency: formData.frequency,
            donorName: formData.donorName,
            donorEmail: formData.donorEmail,
            message: formData.message,
            anonymous: formData.anonymous
          })
        });

        const data = await response.json();
        
        if (data.error) {
          throw new Error(data.error);
        }

        setClientSecret(data.clientSecret);
        setShowPaymentForm(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
        setShowPaymentForm(false);
      } finally {
        setIsProcessing(false);
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(createPaymentIntent, 500);
    return () => clearTimeout(timeoutId);
  }, [currentStep, formData.amount, formData.customAmount, formData.isRecurring, formData.frequency, formData.donorEmail, formData.donorName, formData.message, formData.anonymous, getEffectiveAmount]);



  const getFrequencyLabel = () => {
    return formData.frequency === 'monthly' ? 'month' : 'year';
  };

  const getFrequencyMultiplier = () => {
    return formData.frequency === 'monthly' ? 12 : 1;
  };

  const getAnnualAmount = () => {
    const amount = getEffectiveAmount();
    return amount * getFrequencyMultiplier();
  };

  const canProceedToPayment = () => {
    const amount = getEffectiveAmount();
    return validateDonationAmount(amount) && formData.donorEmail.trim() !== '';
  };

  const handleNextStep = () => {
    if (canProceedToPayment()) {
      setCurrentStep('payment');
      setError('');
      // Scroll to top of the form
      const formElement = document.querySelector('.donate-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleBackStep = () => {
    setCurrentStep('details');
    setError('');
    setShowPaymentForm(false);
    setClientSecret('');
    // Scroll to top of the form
    const formElement = document.querySelector('.donate-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getRecurringBreakdown = () => {
    const amount = getEffectiveAmount();
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
    const nextYear = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
    
    return {
      today: today.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      nextDate: formData.frequency === 'monthly' 
        ? nextMonth.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : nextYear.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      amount: amount.toFixed(2),
      annualTotal: getAnnualAmount().toFixed(2)
    };
  };

  // Payment Form Component
  const PaymentForm: React.FC<{ clientSecret: string }> = ({ clientSecret }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');


    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      if (!stripe || !elements) {
        return;
      }

      setIsProcessing(true);
      setError('');

      // Extract payment intent ID from client secret
      const paymentIntentId = clientSecret.split('_secret_')[0];
      
      try {
        // Submit donation data to Firestore first
        await submitDonationToFirestore({
          amount: getEffectiveAmount(),
          customAmount: formData.customAmount,
          isRecurring: formData.isRecurring,
          frequency: formData.frequency,
          donorName: formData.donorName,
          donorEmail: formData.donorEmail,
          message: formData.message,
          anonymous: formData.anonymous,
          paymentIntentId
        });

        // Store donation details in localStorage for success page
        localStorage.setItem('lastDonationAmount', getEffectiveAmount().toString());
        localStorage.setItem('lastDonorName', formData.anonymous ? 'Anonymous' : formData.donorName);

        // Then process payment with Stripe
        const { error: submitError } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/donate/success?payment_intent=${paymentIntentId}&amount=${getEffectiveAmount()}&donorName=${encodeURIComponent(formData.anonymous ? 'Anonymous' : formData.donorName)}&isRecurring=${formData.isRecurring}&frequency=${formData.frequency}`,
          },
        });

        if (submitError) {
          setError(submitError.message || 'An error occurred');
          setIsProcessing(false);
        }
      } catch (error) {
        setError(error instanceof Error ? error.message : 'An error occurred');
        setIsProcessing(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="payment-form">
        <div className="payment-method-section">
          <h3>Payment Information</h3>
          


          {/* Payment Element */}
          <div className="payment-element-container">
            <PaymentElement 
              options={{
                paymentMethodOrder: ['card', 'us_bank_account', 'apple_pay', 'google_pay'],
                defaultValues: {
                  billingDetails: {
                    name: formData.donorName || '',
                    email: formData.donorEmail || '',
                  },
                },
                layout: {
                  type: 'tabs',
                  defaultCollapsed: false,
                  spacedAccordionItems: false,
                },
                fields: {
                  billingDetails: {
                    name: 'auto',
                    email: 'auto',
                  },
                },
              }}
            />
          </div>
        </div>
        
        {/* Billing Information */}
        <div className="billing-section">
          <h3>Billing Information</h3>
          <div className="billing-fields">
            <div className="form-group">
              <label htmlFor="billingName">Full Name</label>
              <input
                type="text"
                id="billingName"
                defaultValue={formData.donorName}
                placeholder="Enter your full name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="billingEmail">Email</label>
              <input
                type="email"
                id="billingEmail"
                defaultValue={formData.donorEmail}
                placeholder="Enter your email"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        <Button
          type="submit"
          disabled={!stripe || isProcessing}
          variant="primary"
          size="large"
          className="donate-button"
        >
          {isProcessing ? 'Processing...' : `Donate ${formData.isRecurring ? `$${getEffectiveAmount().toFixed(2)}/${getFrequencyLabel()}` : `$${getEffectiveAmount().toFixed(2)}`}`}
        </Button>
      </form>
    );
  };

  return (
    <div className="donate-page">
      <Helmet>
        <title>Donate – Carly Faye Foundation</title>
        <meta name="description" content="Support the Carly Faye Foundation. Your donation empowers young women through academic and athletic scholarships." />
        <link rel="canonical" href="https://carly-faye-foundation.web.app/donate" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Donate – Carly Faye Foundation" />
        <meta property="og:description" content="Your donation helps fund scholarships and programs for young women." />
        <meta property="og:url" content="https://carly-faye-foundation.web.app/donate" />
        <meta property="og:image" content="/images/carly-faye-foundation-hero.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Donate – Carly Faye Foundation" />
        <meta name="twitter:description" content="Support the Carly Faye Foundation and empower young women." />
        <meta name="twitter:image" content="/images/carly-faye-foundation-hero.jpg" />
      </Helmet>
                       <section className="donate-hero">
                   <div className="donate-hero-background">
                     <img src="/images/carly-faye-foundation-hero.jpg" alt="" className="donate-hero-image" />
                     <div className="donate-hero-overlay"></div>
                   </div>
                   <div className="container">
                     <motion.div 
                       initial={{ opacity: 0, y: 20 }}
                       animate={{ opacity: 1, y: 0 }}
                       transition={{ duration: 0.8 }}
                       className="donate-hero-content"
                     >
                       <div className="hero-icon">
                         <Heart size={48} />
                       </div>
                       <h1>Support Our Mission</h1>
                       <p>
                       Your gift fuels Carly’s legacy—empowering young women to rise above life’s challenges, dream big, and pursue their passions. 
                       Through scholarships and our GLO program, your support restores hope, builds confidence, and helps girls turn their obstacles into stepping stones toward a brighter future.
                       </p>
                     </motion.div>
                   </div>
                 </section>

                       <section className="donate-form-section">
                   <div className="container">
                     <div className="donate-form-container">
                       <motion.div 
                         initial={{ opacity: 0, x: -20 }}
                         animate={{ opacity: 1, x: 0 }}
                         transition={{ duration: 0.8, delay: 0.2 }}
                         className="donate-form"
                       >
                         {/* Step Indicator */}
                         <div className="step-indicator">
                           <div className={`step ${currentStep === 'details' ? 'active' : ''}`}>
                             <span className="step-number">1</span>
                             <span className="step-label">Donation Details</span>
                           </div>
                           <div className="step-connector"></div>
                           <div className={`step ${currentStep === 'payment' ? 'active' : ''}`}>
                             <span className="step-number">2</span>
                             <span className="step-label">Payment</span>
                           </div>
                         </div>

                         <div className="form-header">
                           <h2>{currentStep === 'details' ? 'Make a Donation' : 'Complete Payment'}</h2>
                           <p>{currentStep === 'details' ? 'Choose your donation type and amount' : 'Enter your payment information'}</p>
                         </div>

                         {/* Step 1: Donation Details */}
                         {currentStep === 'details' && (
                           <motion.div
                             initial={{ opacity: 0, x: -20 }}
                             animate={{ opacity: 1, x: 0 }}
                             exit={{ opacity: 0, x: 20 }}
                             transition={{ duration: 0.3 }}
                           >
                             {/* Donation Type Toggle */}
                             <div className="donation-type-toggle">
                               <button
                                 className={`toggle-btn ${!formData.isRecurring ? 'active' : ''}`}
                                 onClick={() => handleInputChange('isRecurring', false)}
                               >
                                 <CreditCard size={20} />
                                 <span>One-Time</span>
                               </button>
                               <button
                                 className={`toggle-btn ${formData.isRecurring ? 'active' : ''}`}
                                 onClick={() => handleInputChange('isRecurring', true)}
                               >
                                 <Calendar size={20} />
                                 <span>Recurring</span>
                               </button>
                             </div>

                             {/* Recurring Frequency (if recurring) */}
                             {formData.isRecurring && (
                               <motion.div 
                                 initial={{ opacity: 0, height: 0 }}
                                 animate={{ opacity: 1, height: 'auto' }}
                                 exit={{ opacity: 0, height: 0 }}
                                 className="frequency-selector"
                               >
                                 <label>Frequency:</label>
                                 <div className="frequency-options">
                                   <button
                                     className={`frequency-btn ${formData.frequency === 'monthly' ? 'active' : ''}`}
                                     onClick={() => handleInputChange('frequency', 'monthly')}
                                   >
                                     Monthly
                                   </button>
                                   <button
                                     className={`frequency-btn ${formData.frequency === 'yearly' ? 'active' : ''}`}
                                     onClick={() => handleInputChange('frequency', 'yearly')}
                                   >
                                     Yearly
                                   </button>
                                 </div>
                               </motion.div>
                             )}

                             {/* Donation Amounts */}
                             <div className="donation-amounts">
                               <label>Select Amount:</label>
                               <div className="amount-grid">
                                 {DONATION_AMOUNTS.map((option) => (
                                   <button
                                     key={option.value}
                                     className={`amount-btn ${formData.amount === option.value && !formData.customAmount ? 'active' : ''}`}
                                     onClick={() => handleAmountChange(option.value)}
                                   >
                                     {option.label}
                                   </button>
                                 ))}
                               </div>
                             </div>

                             {/* Custom Amount */}
                             <div className="custom-amount">
                               <label htmlFor="customAmount">Or enter a custom amount:</label>
                               <div className="custom-amount-input">
                                 <DollarSign size={20} />
                                 <input
                                   type="number"
                                   id="customAmount"
                                   value={formData.customAmount}
                                   onChange={(e) => handleCustomAmountChange(e.target.value)}
                                   placeholder="Enter amount"
                                   min="1"
                                   max="10000"
                                   step="0.01"
                                 />
                               </div>
                             </div>

                             {/* Donor Information */}
                             <div className="donor-info">
                               <h3>Your Information</h3>
                               
                               <div className="form-row">
                                 <div className="form-group">
                                   <label htmlFor="donorName">Name (Optional)</label>
                                   <input
                                     type="text"
                                     id="donorName"
                                     value={formData.donorName}
                                     onChange={(e) => handleInputChange('donorName', e.target.value)}
                                     placeholder="Your name"
                                   />
                                 </div>
                                 
                                 <div className="form-group">
                                   <label htmlFor="donorEmail">Email *</label>
                                   <input
                                     type="email"
                                     id="donorEmail"
                                     value={formData.donorEmail}
                                     onChange={(e) => handleInputChange('donorEmail', e.target.value)}
                                     placeholder="your@email.com"
                                     required
                                   />
                                 </div>
                               </div>

                               <div className="form-group">
                                 <label htmlFor="message">Message (Optional)</label>
                                 <textarea
                                   id="message"
                                   value={formData.message}
                                   onChange={(e) => handleInputChange('message', e.target.value)}
                                   placeholder="Share why you're donating..."
                                   rows={3}
                                 />
                               </div>

                               <div className="form-group checkbox-group">
                                 <input
                                   type="checkbox"
                                   id="anonymous"
                                   checked={formData.anonymous}
                                   onChange={(e) => handleInputChange('anonymous', e.target.checked)}
                                 />
                                 <label htmlFor="anonymous">Make this donation anonymous</label>
                               </div>
                             </div>

                             {/* Error Message */}
                             {error && (
                               <motion.div 
                                 initial={{ opacity: 0, y: -10 }}
                                 animate={{ opacity: 1, y: 0 }}
                                 className="error-message"
                               >
                                 {error}
                               </motion.div>
                             )}

                             {/* Donation Summary */}
                             <div className="donation-summary">
                               <h3>Donation Summary</h3>
                               <div className="summary-details">
                                 <div className="summary-row">
                                   <span>Amount:</span>
                                   <span>${getEffectiveAmount().toFixed(2)}</span>
                                 </div>
                                 {formData.isRecurring && (
                                   <>
                                     <div className="summary-row">
                                       <span>Frequency:</span>
                                       <span>{formData.frequency === 'monthly' ? 'Monthly' : 'Yearly'}</span>
                                     </div>
                                     <div className="summary-row total">
                                       <span>Annual Total:</span>
                                       <span>${getAnnualAmount().toFixed(2)}</span>
                                     </div>
                                   </>
                                 )}
                               </div>
                             </div>

                             {/* Next Button */}
                             <div className="form-actions">
                               <Button
                                 onClick={handleNextStep}
                                 disabled={!canProceedToPayment()}
                                 variant="primary"
                                 size="large"
                                 className="next-button"
                               >
                                 Continue to Payment
                               </Button>
                             </div>
                           </motion.div>
                         )}

                         {/* Step 2: Payment Details */}
                         {currentStep === 'payment' && (
                           <motion.div
                             initial={{ opacity: 0, x: 20 }}
                             animate={{ opacity: 1, x: 0 }}
                             exit={{ opacity: 0, x: -20 }}
                             transition={{ duration: 0.3 }}
                           >
                             {/* Recurring Donation Breakdown */}
                             {formData.isRecurring && (
                               <div className="recurring-breakdown">
                                 <h3>Recurring Donation Details</h3>
                                 <div className="breakdown-card">
                                   <div className="breakdown-item">
                                     <span className="label">Today's Charge:</span>
                                     <span className="value">${getRecurringBreakdown().amount}</span>
                                   </div>
                                   <div className="breakdown-item">
                                     <span className="label">Next Charge:</span>
                                     <span className="value">{getRecurringBreakdown().nextDate}</span>
                                   </div>
                                   <div className="breakdown-item">
                                     <span className="label">Annual Total:</span>
                                     <span className="value">${getRecurringBreakdown().annualTotal}</span>
                                   </div>
                                   <div className="breakdown-note">
                                     <p>
                                       You will be charged ${getRecurringBreakdown().amount} today and we will automatically 
                                       charge this same card on {getRecurringBreakdown().nextDate} and each {formData.frequency === 'monthly' ? 'month' : 'year'} thereafter.
                                     </p>
                                   </div>
                                 </div>
                               </div>
                             )}

                             {/* Payment Form */}
                             {showPaymentForm && clientSecret ? (
                               <Elements stripe={stripePromise} options={{ 
                                 clientSecret,
                                 appearance: {
                                   theme: 'stripe',
                                   variables: {
                                     colorPrimary: '#00ccb8',
                                   },
                                 },
                               }}>
                                 <PaymentForm clientSecret={clientSecret} />
                               </Elements>
                             ) : (
                               <div className="payment-form-placeholder">
                                 {isProcessing ? (
                                   <div className="loading-payment">
                                     <div className="loading-spinner"></div>
                                     <p>Preparing payment form...</p>
                                   </div>
                                 ) : (
                                   <div className="payment-form-info">
                                     <p>Loading payment form...</p>
                                   </div>
                                 )}
                               </div>
                             )}

                             {/* Back Link */}
                             <div className="form-actions">
                               <button
                                 onClick={handleBackStep}
                                 className="back-link"
                               >
                                 ← Back to Details
                               </button>
                             </div>

                             <div className="secure-notice">
                               <CheckCircle size={16} />
                               <span>Secure payment powered by Stripe</span>
                             </div>
                           </motion.div>
                         )}
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="donate-info"
            >
              <div className="info-card">
                <h3>How Your Donation Helps</h3>
                <ul>
                  <li>Provides academic scholarships to deserving students</li>
                  <li>Supports athletic scholarships for volleyball players</li>
                  <li>Helps cover the costs of our GLO program</li>
                  <li>Funds educational programs and mentorship</li>
                  <li>Helps young women pursue their dreams</li>
                </ul>
              </div>

              <div className="info-card">
                <h3>Recurring Donations</h3>
                <p>
                  Monthly or yearly recurring donations provide consistent support 
                  and help us plan our scholarship programs more effectively.
                </p>
                <ul>
                  <li>Cancel anytime</li>
                  <li>Modify amount or frequency</li>
                  <li>Tax-deductible</li>
                </ul>
              </div>

              <div className="info-card">
                <h3>Tax Information</h3>
                <p>
                  The Carly Faye Foundation is a 501(c)(3) nonprofit organization. 
                  Your donation is tax-deductible to the extent allowed by law.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DonatePage; 